import React from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Input } from 'antd';
import PhoneInput from 'react-phone-number-input/input';
import { isValidPhoneNumber } from 'react-phone-number-input';

/**
 * Optional 2FA cell number: enter a number, receive a code, verify it.
 *
 * Presentational. The container owns sendOtpToSms/verifySmsOtp and the
 * skip-with-warning rule, because the cell number is persisted ONLY by the OTP
 * verify call — a texted-but-unconfirmed number is saved nowhere.
 */
const SecurityStep = ({
  phoneStep, phoneDraft, phoneCode, phoneError, phoneBusy,
  onPhoneChange, onCodeChange, onSendCode, onVerifyCode,
}) => {
  const phoneIsValid = !!phoneDraft && isValidPhoneNumber(phoneDraft);
  return (
    <div className="walkthrough-compact-step walkthrough-security-step">
      <h2>Account security</h2>
      <p>
        Optionally add a cell number for two-factor authentication (2FA), an extra sign-in
        verification step. Once it&apos;s verified, you can choose to have your sign-in code
        texted to this number instead of emailed to you.
      </p>
      <Card size="small" className="walkthrough-security-card">
        {phoneStep === 'saved' ? (
          <div className="walkthrough-phone-saved">
            <strong>Cell number verified</strong>
            <span>
              A cell number is saved for two-factor authentication. At sign-in you can choose
              to have your code texted to it instead of emailed to you.
            </span>
          </div>
        ) : (
          <>
            <span className="walkthrough-security-label">US cell number</span>
            <PhoneInput
              id="walkthrough-phone-number"
              aria-label="US cell number"
              className="ant-input walkthrough-phone-input"
              country="US"
              disabled={phoneBusy || phoneStep === 'code'}
              international
              placeholder="(555) 555-0100"
              value={phoneDraft}
              onChange={onPhoneChange}
            />
            {phoneStep === 'code' && (
              <>
                <span className="walkthrough-security-label">6-digit verification code</span>
                <Input
                  id="walkthrough-phone-code"
                  aria-label="6-digit verification code"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={phoneCode}
                  onChange={onCodeChange}
                />
              </>
            )}
            {phoneError && <div className="walkthrough-inline-error">{phoneError}</div>}
            {phoneStep === 'code' ? (
              <div className="walkthrough-security-actions">
                <Button type="primary" disabled={phoneCode.length !== 6} loading={phoneBusy} onClick={onVerifyCode}>Verify code</Button>
                <Button type="link" disabled={phoneBusy} onClick={onSendCode}>Resend code</Button>
              </div>
            ) : (
              <Button type="primary" disabled={!phoneIsValid} loading={phoneBusy} onClick={onSendCode}>Text me a code</Button>
            )}
          </>
        )}
      </Card>
      <p className="walkthrough-optional-note">You can skip this for now and add a cell number later in Settings.</p>
    </div>
  );
};

SecurityStep.defaultProps = {
  phoneStep: 'number',
  phoneDraft: '',
  phoneCode: '',
  phoneError: '',
  phoneBusy: false,
};

SecurityStep.propTypes = {
  phoneStep: PropTypes.string,
  phoneDraft: PropTypes.string,
  phoneCode: PropTypes.string,
  phoneError: PropTypes.string,
  phoneBusy: PropTypes.bool,
  onPhoneChange: PropTypes.func.isRequired,
  onCodeChange: PropTypes.func.isRequired,
  onSendCode: PropTypes.func.isRequired,
  onVerifyCode: PropTypes.func.isRequired,
};

export default SecurityStep;
